<template>
    <c-modal v-model="isModalOpen" title="Import Layers" size="lg" @ok="handleClose" @close="handleClose">
        <div v-if="showLoader" class="align-items-center d-flex justify-content-center my-2 text-center my-2">
            <b-spinner variant="primary" />
            <strong class="ml-3 primary-text">{{ loaderMessage }}</strong>
        </div>
        <b-tabs v-model="selectedTab" :nav-class="isLayersTab ? 'border-0' : ''">
            <b-tab
                v-if="!showLoader"
                title="Designs"
                :title-item-class="isLayersTab || selectedTab == Tabs.Canva ? 'hidden' : ''"
            >
                <modal-import-tabs-designs @design-selected="changeTab(Tabs.DesignsLayers)" />
            </b-tab>
            <b-tab v-if="!showLoader" title="Designs layers" title-item-class="hidden">
                <template v-if="selectedTab === Tabs.DesignsLayers">
                    <modal-import-tabs-layers
                        v-if="template && selectedItem"
                        :type="LayerType.DESIGN"
                        :raw-element="template"
                    />
                </template>
            </b-tab>
            <b-tab
                v-if="!showLoader"
                title="Templates"
                :title-item-class="isLayersTab || selectedTab == Tabs.Canva ? 'hidden' : ''"
            >
                <modal-import-tabs-templates @template-selected="changeTab(Tabs.TemplatesLayers)" />
            </b-tab>
            <b-tab v-if="!showLoader" title="Template layers" title-item-class="hidden">
                <template v-if="selectedTab === Tabs.TemplatesLayers">
                    <modal-import-tabs-layers
                        v-if="template && selectedItem"
                        :type="LayerType.TEMPLATE"
                        :raw-element="template"
                    />
                </template>
            </b-tab>
            <b-tab v-if="!showLoader" title="Canva Designs" title-item-class="hidden">
                <template v-if="selectedTab === Tabs.Canva">
                    <modal-import-tabs-canva @canva-design-selected="changeTab(Tabs.ExternalLayers)" />
                </template>
            </b-tab>
            <b-tab v-if="!showLoader" title="PSD layers" title-item-class="hidden">
                <template v-if="selectedTab === Tabs.ExternalLayers">
                    <modal-import-tabs-external-layers
                        v-if="parsedFileLayers"
                        @fire-import-layers="handleImportLayers"
                    />
                </template>
            </b-tab>
            <b-tab v-if="!showLoader" title="Amazon XLSX" title-item-class="hidden">
                <template v-if="selectedTab === Tabs.AmazonFileXLSX">
                    <amazon-import-tabs-modal @fire-import-layers="handleImportLayers" />
                </template>
            </b-tab>
        </b-tabs>

        <template #modal-footer>
            <div v-if="isTemplateDesignsLayersTab" class="d-flex w-100 mb-3 flex-column align-items-start">
                <c-checkbox v-model="preserveId" label="Keep layer / group ID when importing." switch />
                <c-checkbox v-model="preserveGroups" label="Keep layer group when importing." switch />
            </div>
            <div class="d-flex justify-content-between w-100">
                <button class="btn btn-cancel" @click="handleClose">Cancel</button>

                <div v-if="isLayersTab" class="d-flex">
                    <button class="btn btn-outline-primary" @click="handleBack">Back</button>

                    <div v-if="parsedFileLayers?.mode !== 'PNG'" class="ml-2">
                        <b-overlay
                            :show="isLoading"
                            rounded
                            opacity="0.6"
                            spinner-small
                            spinner-variant="primary"
                            class="d-inline-block"
                        >
                            <button
                                class="btn btn-primary"
                                :disabled="!hasValidSelectedTreeItems || isLoading"
                                @click="handleImportLayers"
                            >
                                Import
                            </button>
                        </b-overlay>
                    </div>
                </div>
            </div>
        </template>
    </c-modal>
</template>

<script lang="ts" setup>
//style="width: 136px"
import { computed, ref, watch } from 'vue';
import { LayerType, Tabs, TreeItem, TreeType } from '../types';
import { CanvasElement } from '@/typings/customilyElements/canvasElements';
import { SignedUrlResponse } from '@/api/typings/image';
import { getImageUploadS3SignedUrl } from '@/api/image';
import { uploadFileToS3 } from '@/utils/s3Utils';
import store from '@/store';
import ModalImportTabsDesigns from './tabs/designs/ModalImportTabsDesigns.vue';
import ModalImportTabsCanva from './tabs/canva/ModalImportTabsCanva.vue';
import ModalImportTabsTemplates from './tabs/templates/ModalImportTabsTemplates.vue';
import ModalImportTabsLayers from './tabs/layers/ModalImportTabsLayers.vue';
import ModalImportTabsExternalLayers from './tabs/external-layers/ModalImportTabsExternalLayers.vue';
import AmazonImportTabsModal from './tabs/amazon/AmazonImportTabsModal.vue';
import CModal from '@/components/CModal.vue';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import { BSpinner, BTab } from 'bootstrap-vue';
import { ImportLayersSource } from '@/typings/layers/layers';
import { useFonts } from '@/composables/fonts';
import type { CanvaFont } from '@/typings/layers/layers';
import { uploadVectorToS3 } from '@/utils/vectorUtils';

const fonts = useFonts();

const emit = defineEmits(['import-layers']);

const props = defineProps<{
    isOpen: boolean;
    preSelectedTab?: Tabs;
    handleOpen: () => void;
    handleClose: () => void;
}>();

const showLoader = ref(false);
const loaderMessage = ref('Loading...');
const selectedTab = ref(Tabs.Templates);
const preserveId = ref(true);
const preserveGroups = ref(true);
const isLoading = ref(false);

const selectedItem = computed(() => store.state.storeLayersModal?.selectedItem);
const template = computed(() => store.state.storeLayersModal!.template);
const parsedFileLayers = computed(() => store.state.storeLayersModal!.parsedFileLayers);
const selectedTreeItems = computed(() => store.state.storeLayersModal!.selectedTreeItems);

const isLayersTab = computed(() => {
    return [Tabs.DesignsLayers, Tabs.TemplatesLayers, Tabs.ExternalLayers, Tabs.AmazonFileXLSX].includes(
        selectedTab.value
    );
});

const isTemplateDesignsLayersTab = computed(() => {
    return [Tabs.DesignsLayers, Tabs.TemplatesLayers].includes(selectedTab.value);
});

const isValidFileSelectedTreeItem = computed(() => {
    return selectedTreeItems.value.some(
        (item) =>
            (selectedTab.value === Tabs.ExternalLayers || selectedTab.value === Tabs.AmazonFileXLSX) &&
            [
                TreeType.FROM_FILE_TEXT_ELEMENT,
                TreeType.FROM_FILE_IMAGE_ELEMENT,
                TreeType.FROM_FILE_IMAGE_PLACEHOLDER_ELEMENT,
                TreeType.FROM_FILE_VECTOR_ELEMENT,
                TreeType.FROM_FILE_VECTOR_PLACEHOLDER_ELEMENT,
                TreeType.FROM_FILE_CALENDAR_ELEMENT,
                TreeType.FROM_FILE_MAP_ELEMENT,
                TreeType.FROM_FILE_MOON_PHASE_ELEMENT,
                TreeType.FROM_FILE_STAR_MAP_ELEMENT
            ].includes(item.type)
    );
});

const isModalOpen = computed({
    get(): boolean {
        return props.isOpen;
    },
    set(value: boolean) {
        if (value) {
            props.handleOpen();
        } else {
            resetModal();
        }
    }
});

const hasValidSelectedTreeItems = computed(() =>
    selectedTreeItems.value.some((item) => item.type === TreeType.CANVAS_ELEMENT || isValidFileSelectedTreeItem.value)
);

watch(
    () => props.preSelectedTab,
    (newTab) => {
        if (!newTab) return;
        selectedTab.value = newTab;
    },
    { immediate: true }
);

const changeTab = (tab: Tabs) => {
    if (tab === Tabs.Designs || tab === Tabs.Templates || tab === Tabs.Canva) {
        clearSelectedItem();
        clearSelectedTreeItems();
    }
    selectedTab.value = tab;
};

const handleBack = () => {
    switch (selectedTab.value) {
        case Tabs.DesignsLayers:
            changeTab(Tabs.Designs);
            break;
        case Tabs.TemplatesLayers:
            changeTab(Tabs.Templates);
            break;
        case Tabs.ExternalLayers:
            if (parsedFileLayers.value?.type == 'CANVA') {
                clearParsedFileLayers();
                changeTab(Tabs.Canva);
            } else resetModal();
            break;
        case Tabs.AmazonFileXLSX:
            resetModal();
            break;
    }
};

const getTreeItemsByType = (type: TreeType): TreeItem[] => {
    return selectedTreeItems.value.filter((item) => item.type === type);
};

const resetModal = () => {
    isLoading.value = false;
    showLoader.value = false;
    clearSelectedItem();
    clearSelectedTreeItems();
    clearParsedFileLayers();
    selectedTab.value = Tabs.Templates;
    preserveId.value = true;
    preserveGroups.value = true;
    props.handleClose();
};

const handleImportLayers = async () => {
    try {
        isLoading.value = true;
        let layersSource: ImportLayersSource = ImportLayersSource.CUSTOMILY;

        if (selectedTab.value == Tabs.ExternalLayers) {
            if (parsedFileLayers.value?.type == 'CANVA') layersSource = ImportLayersSource.CANVA;
            else if (parsedFileLayers.value?.type == 'PSD') layersSource = ImportLayersSource.PSD;
        } else if (selectedTab.value == Tabs.AmazonFileXLSX) {
            layersSource = ImportLayersSource.AMAZON;
        }

        if (layersSource !== ImportLayersSource.CUSTOMILY) {
            const elements = selectedTreeItems.value;
            const dynamicImagePromisesParameters: [SignedUrlResponse, Blob][] = [];
            const dynamicVectorPromises: Promise<void>[] = [];
            const notFoundFontsSet = new Set<string>();
            const notFoundFonts: CanvaFont[] = [];
            const saveVectorPathIntoElement = async (svgElement: TreeItem, svgFile: File) => {
                if (!svgElement.metadata) return;
                const { SVG } = await uploadVectorToS3(svgFile, false, false, 'vector-svg');
                svgElement.metadata.svgPath = SVG;
            };
            for (const element of elements) {
                switch (element.type) {
                    case TreeType.FROM_FILE_CURVED_TEXT_ELEMENT:
                    case TreeType.FROM_FILE_TEXT_ELEMENT: {
                        if (!element.metadata?.font) continue;
                        const { family, style, rawTypeface } = element.metadata.font;
                        const found = fonts.userFonts.value.find((f) => f.Name === family && f.Style === style);
                        if (!found && !notFoundFontsSet.has(rawTypeface)) {
                            notFoundFontsSet.add(rawTypeface);
                            notFoundFonts.push(element.metadata.font);
                        }
                        break;
                    }
                    case TreeType.FROM_FILE_IMAGE_ELEMENT:
                    case TreeType.FROM_FILE_IMAGE_PLACEHOLDER_ELEMENT: {
                        if (!element.metadata?.imgSrc) continue;
                        const response = await getImageUploadS3SignedUrl('preview', 'png');
                        element.metadata.imgUrl = response.Path;
                        dynamicImagePromisesParameters.push([response, element.metadata.imgSrc]);
                        if (!element.metadata?.maskSrc) continue;
                        const maskResponse = await getImageUploadS3SignedUrl('preview', 'png');
                        element.metadata.maskUrl = maskResponse.Path;
                        dynamicImagePromisesParameters.push([maskResponse, element.metadata.maskSrc]);
                        break;
                    }
                    case TreeType.FROM_FILE_VECTOR_ELEMENT: {
                        if (!element.metadata?.svgString) continue;
                        const xmlSvg = `<?xml version="1.0" encoding="UTF-8"?>\n${element.metadata.svgString}`;
                        const svgFile = new File([xmlSvg], `${element.name}.svg`, {
                            type: 'image/svg+xml;charset=utf-8',
                            lastModified: Date.now()
                        });
                        dynamicVectorPromises.push(saveVectorPathIntoElement(element, svgFile));
                        break;
                    }
                }
            }

            const dynamicImagePromises = dynamicImagePromisesParameters.map((tuple) => {
                const { SignedUrl, Mime, CacheControl } = tuple[0];
                return uploadFileToS3(SignedUrl, Mime, tuple[1], CacheControl);
            });

            await Promise.all([...dynamicImagePromises, ...dynamicVectorPromises]);

            const { selectedPage, pages, importedFileName, mode } = parsedFileLayers.value!;
            const page = pages.find((p) => p.pageNumber == selectedPage)!;
            const pageDimensions = parsedFileLayers.value?.includeDimensions
                ? {
                      width: page.artWorkWidth,
                      height: page.artWorkHeight
                  }
                : undefined;
            const importAsPng = mode === 'PNG';
            emit('import-layers', {
                from: layersSource,
                notFoundFonts,
                fileElements: elements,
                fileDimensions: pageDimensions,
                pageBackgroundColor: !importAsPng ? page.backgroundColor : null,
                asPng: importAsPng,
                importedFileName,
                preserveId: true,
                handleClose: () => {
                    resetModal();
                },
                handleError: () => {
                    isLoading.value = false;
                }
            });
        } else {
            const treeItems = getTreeItemsByType(TreeType.CANVAS_ELEMENT);
            const treeItemUuids = treeItems.map((item) => item.uuid);

            const canvasElements = getCanvasElementsByUuids(treeItemUuids);
            const { textsEps, circularTextsEps, imagePlaceHoldersEps, vectorEPSEps } = canvasElements;
            const elementGroups = new Set(
                [textsEps, circularTextsEps, imagePlaceHoldersEps, vectorEPSEps].flatMap((elements) =>
                    elements.map((element) => element.groupId)
                )
            );
            const canvasGroups = template.value?.groups.filter((group) => elementGroups.has(group.id));
            emit('import-layers', {
                from: layersSource,
                canvasElements: canvasElements,
                preserveId: preserveId.value,
                preserveGroups: preserveGroups.value,
                canvasGroups,
                handleClose: () => {
                    resetModal();
                },
                handleError: () => {
                    isLoading.value = false;
                }
            });
        }
    } catch (error) {
        console.error('Error importing layers:', error);
        isLoading.value = false;
    }
};

const getCanvasElementsByUuids = (uuids: (string | number | undefined)[]) => {
    let textsEpsElements: CanvasElement[] = [];
    let circularTextsEpsElements: CanvasElement[] = [];
    let imagePlaceHoldersEpsElements: CanvasElement[] = [];
    let vectorEPSEpsElements: CanvasElement[] = [];

    for (const eps of template.value?.listEPS || []) {
        textsEpsElements = [...textsEpsElements, ...filterCanvasElementsByUuids(eps.textsEps, uuids)];
        circularTextsEpsElements = [
            ...circularTextsEpsElements,
            ...filterCanvasElementsByUuids(eps.circularTextsEps, uuids)
        ];
        imagePlaceHoldersEpsElements = [
            ...imagePlaceHoldersEpsElements,
            ...filterCanvasElementsByUuids(eps.imagePlaceHoldersEps, uuids)
        ];
        vectorEPSEpsElements = [...vectorEPSEpsElements, ...filterCanvasElementsByUuids(eps.vectorEPSEps, uuids)];
    }

    const elements = {
        textsEps: textsEpsElements,
        circularTextsEps: circularTextsEpsElements,
        imagePlaceHoldersEps: imagePlaceHoldersEpsElements,
        vectorEPSEps: vectorEPSEpsElements
    };
    return elements;
};

const filterCanvasElementsByUuids = (canvasElements: CanvasElement[], uuids: (string | number | undefined)[]) => {
    return canvasElements.filter((item) => uuids.includes(item.uuid));
};

const clearSelectedItem = () => {
    store.dispatch('storeLayersModal/clearSelectedItem');
};
const clearSelectedTreeItems = () => {
    store.dispatch('storeLayersModal/clearSelectedTreeItems');
};
const clearParsedFileLayers = () => {
    store.dispatch('storeLayersModal/clearParsedFileLayers');
};
</script>

<style lang="scss" scoped>
$btn-cancel: #e9e9e9;
$btn-cancel-hover: #e9e9e9;

.vue-need-bootstrap {
    .btn {
        padding: 0.25rem 1rem;
        &.btn-cancel {
            background-color: $btn-cancel !important;
            color: #000;
            font-weight: bold;
            border-color: $btn-cancel !important;
            &:hover {
                background-color: $btn-cancel-hover !important;
                border-color: $btn-cancel-hover !important;
            }
        }
    }
}

::v-deep .vue-need-bootstrap .nav-tabs .nav-link {
    color: #495057 !important;
}
::v-deep .vue-need-bootstrap .nav-tabs .nav-link.active {
    color: var(--primary-purple) !important;
}

.upload-psd {
    border: 2px solid #7258e0 !important;
    color: #7258e0 !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
    position: relative !important;
    padding: 0.25rem 1rem;
    border-radius: 0.25rem;
    text-align: center;
    vertical-align: middle;
    user-select: none;
    background-color: rgba(0, 0, 0, 0);

    &:hover {
        box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12);
    }
    .start {
        position: absolute;
        top: -11px;
        right: -12px;
    }
}
</style>
